// @flow
/** @jsx jsx */

import { jsx } from "@emotion/core";

import * as React from "react";
import { ApolloClient } from "apollo-client";
import { InMemoryCache } from "apollo-cache-inmemory";
import { HttpLink } from "apollo-link-http";
import { onError } from "apollo-link-error";
import { ApolloLink } from "apollo-link";
import { ApolloProvider, Query } from "react-apollo";
import createHistory from "history/createBrowserHistory";
import fetch from "unfetch";
import { Router } from "react-router-dom";
import * as Sentry from "@sentry/react";
import "wijmo/styles/wijmo.css";
import "react-toastify/dist/ReactToastify.css";

import { HistoryContext } from "../context/history.context";
import { AuthConsumer, AuthProvider } from "../context/auth.context";
// $FlowFixMe
import graphqlConfig from "../config/graphql";
import LoadingAsync from "../components/LoadingAsync";
import isLoggedInQuery from "../graphql/queries/isLoggedIn.query";

import "../static/shared/css/bootstrap.min.css";
import logger from "../utils/logger";

const history = createHistory();

const isLoggedInLink = new ApolloLink((operation, forward) =>
  forward(operation).map(response => {
    if (response.data.logout && response.data.logout.isLoggedIn === false) {
      window.location.reload()
    }
    return response;
  })
);

const cache = new InMemoryCache();

const client = new ApolloClient({
  link: ApolloLink.from([
    onError(({ graphQLErrors, networkError, response, operation }) => {
      if (Array.isArray(graphQLErrors)) {
        graphQLErrors.map(({ message, locations, data, time_thrown, path, extensions, name }) => {
          if (message === "Context creation failed: Unauthorized") {
            window.location.reload()
          }

          if (data && time_thrown) {
            const { operationName } = operation;
            // this is a convention for returning errors from the BE.
            // `response` is what the schema for the query/mutation was
            // waiting and `extraInfo` is anything else that the resolver
            // wanted to send back and that is not part of the schema
            const { response: res } = data;

            if (window.Rollbar && typeof window.Rollbar.error === "function") {
              window.Rollbar.error(message, {
                message,
                data,
                time_thrown,
                path,
                locations
              });
            }

            response.errors = null;
            response.data[operationName] = {
              ...res,
              __typename: operationName,
              error: {
                message,
                data,
                time_thrown
              }
            };
          }

          logger.error(
            `[GQL] Message ${message}, Location ${locations}, Path ${path}`
          );

          return message;
        });
      }

      if (networkError) {
       const errorDetails = {
          message: networkError.message || 'No message available',
          statusCode: networkError.statusCode || 'No status code available',
          result: networkError.result || 'No result available',
          response: networkError.response ? {
            status: networkError.response.status,
            headers: networkError.response.headers,
            body: networkError.response.body
          } : 'No response available',
          stack: networkError.stack || 'No stack trace available',
          operation: operation.operationName || 'No operation name available',
          variables: operation.variables || 'No variables available'
        };

        logger.error(`[GQL] [Network error]`, errorDetails);

        // Capture as a handled warning with rich context. This is the single,
        // canonical Sentry record for Apollo network errors. The Sentry config
        // in App.js drops any duplicate auto-captured copies (e.g. from the
        // global unhandledrejection path) using the `error.type` tag set here.
        try {
          Sentry.withScope((scope) => {
            scope.setLevel('warning');
            scope.setTag('error.type', 'apollo_network_error');
            scope.setTag(
              'graphql.operation',
              (operation && operation.operationName) || 'unknown'
            );
            scope.setContext('apollo', {
              operationName: (operation && operation.operationName) || null,
              statusCode: errorDetails.statusCode,
              url:
                typeof window !== 'undefined' && window.location
                  ? window.location.href
                  : null,
            });
            scope.setFingerprint([
              'apollo-network-error',
              (operation && operation.operationName) || 'unknown',
            ]);
            Sentry.captureException(networkError);
          });
        } catch (sentryErr) {
          // Never let Sentry capture itself throw into the error pipeline.
          logger.error('[Sentry] capture failed for network error', sentryErr);
        }
     }
    }),
    isLoggedInLink,
    new HttpLink({
      ...graphqlConfig,
      fetch
    })
  ]),
  cache
});

type Props = {
  children: (childrenProps: {
    isLoggedIn: boolean,
  }) => React.Node,
  isFullPage?: boolean,
};

class Base extends React.PureComponent<Props> {

  static getClient() {
    return client;
  }

  render() {
    return (
      <ApolloProvider client={client}>
        <HistoryContext.Provider value={history}>
          <Query query={isLoggedInQuery} fetchPolicy="network-only">
            {({ data, error, loading }: any) => {
              if (loading)
                return (
                  <div
                    css={{
                      width: "100vw",
                      height: "100vh",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <LoadingAsync text="Validating Credentials..." />
                  </div>
                );

              return (
                <AuthProvider
                  isLoggedIn={
                    !error && data && data.auth && !!data.auth.isLoggedIn
                  }
                >
                  <AuthConsumer>
                    {({ isLoggedIn }: any) => {
                      return (
                        // $FlowFixMe
                        <Router history={history}>
                          {this.props.children({
                            isLoggedIn
                          })}
                        </Router>
                      );
                    }}
                  </AuthConsumer>
                </AuthProvider>
              );
            }}
          </Query>
        </HistoryContext.Provider>
      </ApolloProvider>
    );
  }
}

export default Base;
